/**
 * Fatura do mesmo cartão+mês criada em dois aparelhos (ADR 0183).
 *
 * Como nasce: cada aparelho cria a linha de `invoices` do (cartão, mês) com id próprio quando a
 * tela do cartão materializa os meses. No apply, a fatura do outro colide no índice único
 * `idx_invoices_card_month` e é pulada (savepoint por linha), mas as mensagens dela ficam no
 * oplog. As compras e os pagamentos do outro chegam apontando pra uma fatura que este aparelho
 * não tem: somem da fatura (relato do Celso, iPhone + iPad, 1.0.70), e o reparo de referência
 * pendurada os conta como `pending` a cada boot (Sentry REACT-NATIVE-32), porque o pai está vivo
 * no oplog e nunca chega à tabela.
 *
 * O conserto escolhe a MESMA sobrevivente nos dois aparelhos (a de menor id) e cada um faz a sua
 * parte:
 * - quem tem a PERDEDORA na tabela adota a sobrevivente: passa as compras, os pagamentos, o
 *   previsto e o `rolled_from` pra ela, apaga a perdedora e grava a sobrevivente a partir do
 *   oplog. As escritas da passagem são CAPTURADAS e viajam (o tombstone da perdedora, inclusive,
 *   é o que fecha o `pending` no outro aparelho). O que a pessoa marcou na perdedora e a
 *   sobrevivente não tem (total ajustado, conferência com o banco, destino do resto) vai junto;
 * - quem tem a SOBREVIVENTE na tabela só religa, sem capturar, o que chegou apontando pra
 *   perdedora, e esconde (também sem capturar) o previsto dela: a sobrevivente já tem o seu.
 *   Capturar aqui empurraria as compras do outro aparelho pra uma fatura que ele, se ainda
 *   estiver num build sem este conserto, não consegue gravar: ele perderia da fatura as
 *   próprias compras. Assim o build antigo fica como estava, e converge quando atualizar.
 *
 * Por fim, o previsto ("Fatura X" pendente na conta de pagamento) fica UM por fatura: os dois
 * aparelhos criam o seu com id próprio e o `ensureInvoiceExpenseTransaction` só cuida do
 * primeiro que acha, então dois previstos contariam a fatura duas vezes no saldo previsto. Fica
 * o de menor id, a mesma escolha nos dois lados, e o apagamento viaja.
 *
 * Roda no ciclo de sync, depois do apply e ainda sob o lock (como o `dedupeOccurrences`): a
 * `sync_guard` já caiu, então o que é capturado sobe no próximo ciclo, e nenhum apply abre por
 * baixo destas escritas. Idempotente: sem fatura fantasma nem previsto duplicado, não escreve.
 */

import { execute, runInTransaction } from '@/db/client';
import { recalculateInvoiceTotal } from '@/db/repositories/invoiceRepository';
import { AdjustmentType } from '@/lib/constants';
import { withCaptureSuspended } from './capture';
import { writeRow } from './drain';
import { materialize, type MaterializedRow } from './engine';
import { TOMBSTONE_COLUMN } from './messages';
import { loadRowMessages } from './persistence';

/** Toda coluna que aponta pra uma fatura. */
const INVOICE_REFERENCES = [
    { table: 'transactions', column: 'invoice_id' },
    { table: 'invoice_payments', column: 'invoice_id' },
    { table: 'invoices', column: 'rolled_from_invoice_id' },
] as const;

/** Previsto do pagamento da fatura, ainda pendente (o que o `ensureInvoiceExpenseTransaction` mantém). */
const PENDING_FORECAST = `adjustment_type = '${AdjustmentType.INVOICE_EXPENSE}' AND is_confirmed = 0 AND deleted_at IS NULL`;

export interface DuplicateInvoiceReport {
    /** Faturas perdedoras trocadas pela sobrevivente neste aparelho. */
    adopted: number;
    /** Linhas que apontavam pra uma perdedora e foram religadas à sobrevivente. */
    relinked: number;
    /** Previstos a mais da mesma fatura que foram apagados. */
    forecasts: number;
}

type Row = Record<string, unknown>;

function rowsOf(result: unknown): Row[] {
    return (result as { rows?: Row[] } | undefined)?.rows ?? [];
}

function affected(result: unknown): number {
    return Number((result as { rowsAffected?: number } | undefined)?.rowsAffected ?? 0);
}

/**
 * Faturas que o oplog dá como VIVAS e que não estão na tabela, em ordem de id (com três aparelhos
 * a menor tem que ser decidida primeiro). Fica de fora a que tem escrita local ainda na outbox:
 * apagada aqui depois do dreno, o tombstone ainda não chegou ao oplog, e ela pareceria fantasma.
 */
async function ghostInvoiceIds(): Promise<string[]> {
    const rows = rowsOf(await execute(
        `SELECT m.row_id AS id FROM sync_messages m
          WHERE m.table_name = 'invoices' AND m.column_name = ?
            AND m.row_id NOT IN (SELECT id FROM invoices)
            AND m.row_id NOT IN (SELECT row_id FROM sync_outbox WHERE table_name = 'invoices')
            AND m.timestamp = (SELECT MAX(t.timestamp) FROM sync_messages t
                                WHERE t.table_name = 'invoices' AND t.row_id = m.row_id AND t.column_name = ?)
            AND m.value = 0
          ORDER BY m.row_id`,
        [TOMBSTONE_COLUMN, TOMBSTONE_COLUMN],
    ));
    return rows.map((r) => String(r.id));
}

/** Religa à `to` tudo que aponta pra `from`. Sem o previsto, quando `skipForecast`. */
async function relink(from: string, to: string, options?: { skipForecast?: boolean }): Promise<number> {
    let total = 0;
    for (const ref of INVOICE_REFERENCES) {
        const skip = options?.skipForecast && ref.table === 'transactions' ? ` AND NOT (${PENDING_FORECAST})` : '';
        total += affected(await execute(
            `UPDATE \`${ref.table}\` SET \`${ref.column}\` = ? WHERE \`${ref.column}\` = ?${skip}`,
            [to, from],
        ));
    }
    return total;
}

/**
 * O que a pessoa marcou na fatura que vai sumir e a sobrevivente não tem. Totais e status ficam
 * de fora: o `recalculateInvoiceTotal` refaz a partir das compras e dos pagamentos.
 */
function carriedOver(loser: Row, survivor: MaterializedRow): Row {
    const patch: Row = {};
    if (survivor.manual_total == null && loser.manual_total != null) {
        patch.manual_total = loser.manual_total;
        patch.is_manually_set = loser.is_manually_set;
    }
    if (loser.reconciled_at != null
        && (survivor.reconciled_at == null || String(loser.reconciled_at) > String(survivor.reconciled_at))) {
        patch.reconciled_at = loser.reconciled_at;
        patch.reconciled_item_count = loser.reconciled_item_count;
    }
    if (survivor.remainder_action == null && loser.remainder_action != null) {
        patch.remainder_amount = loser.remainder_amount;
        patch.remainder_action = loser.remainder_action;
    }
    return patch;
}

async function invoicesWithDuplicatedForecast(): Promise<string[]> {
    return rowsOf(await execute(
        `SELECT invoice_id FROM transactions
          WHERE invoice_id IS NOT NULL AND ${PENDING_FORECAST}
          GROUP BY invoice_id HAVING count(*) > 1`,
    )).map((r) => String(r.invoice_id));
}

/** Deixa um previsto pendente por fatura, o de menor id. */
async function dedupeForecasts(invoiceIds: string[], now: string): Promise<number> {
    let removed = 0;
    for (const invoiceId of invoiceIds) {
        removed += affected(await execute(
            `UPDATE transactions SET deleted_at = ?, updated_at = ?
              WHERE invoice_id = ? AND ${PENDING_FORECAST}
                AND id > (SELECT min(id) FROM transactions WHERE invoice_id = ? AND ${PENDING_FORECAST})`,
            [now, now, invoiceId, invoiceId],
        ));
    }
    return removed;
}

export async function mergeDuplicateInvoices(): Promise<DuplicateInvoiceReport> {
    const report: DuplicateInvoiceReport = { adopted: 0, relinked: 0, forecasts: 0 };
    const ghosts = await ghostInvoiceIds();
    // Sonda barata: no caso normal (nada a fazer) sai sem abrir transação.
    if (ghosts.length === 0 && (await invoicesWithDuplicatedForecast()).length === 0) return report;
    const touched = new Set<string>();
    const now = new Date().toISOString();

    // FK OFF: adotar exige apagar a perdedora antes de gravar a sobrevivente (o índice único não
    // deixa as duas coexistirem), e nesse meio-tempo as compras já apontam pra sobrevivente.
    await runInTransaction(async () => {
        for (const ghost of ghosts) {
            const data = materialize(await loadRowMessages('invoices', ghost)).invoices?.[ghost];
            if (!data || data.credit_card_id == null || data.reference_month == null) continue;
            const [occupant] = rowsOf(await execute(
                'SELECT * FROM invoices WHERE credit_card_id = ? AND reference_month = ?',
                [String(data.credit_card_id), String(data.reference_month)],
            ));
            // Sem ninguém no lugar não foi colisão: não é este o conserto.
            if (!occupant) continue;
            const local = String(occupant.id);

            if (local < ghost) {
                const relinked = await withCaptureSuspended(execute, async () => {
                    await execute(
                        `UPDATE transactions SET deleted_at = ?, updated_at = ? WHERE invoice_id = ? AND ${PENDING_FORECAST}`,
                        [now, now, ghost],
                    );
                    return relink(ghost, local, { skipForecast: true });
                });
                report.relinked += relinked;
                // A fantasma segue viva no oplog até o outro aparelho atualizar; recalcular a
                // cada ciclo sem ter religado nada regravaria o `updated_at` e subiria pra sempre.
                if (relinked > 0) touched.add(local);
                continue;
            }

            report.relinked += await relink(local, ghost);
            await execute('DELETE FROM invoices WHERE id = ?', [local]);
            await withCaptureSuspended(execute, () => writeRow('invoices', ghost, data));
            const patch = carriedOver(occupant, data);
            const columns = Object.keys(patch);
            if (columns.length > 0) {
                await execute(
                    `UPDATE invoices SET ${columns.map((c) => `\`${c}\` = ?`).join(', ')} WHERE id = ?`,
                    [...columns.map((c) => patch[c] as string | number | null), ghost],
                );
            }
            report.adopted += 1;
            touched.add(ghost);
        }

        // Depois das adoções, que podem ter juntado o previsto dos dois na mesma fatura.
        const forecastInvoices = await invoicesWithDuplicatedForecast();
        report.forecasts = await dedupeForecasts(forecastInvoices, now);
        for (const id of forecastInvoices) touched.add(id);
    }, { foreignKeys: 'off' });

    // Fora da transação, como no dedupe: é agregação e seguraria o mutex.
    for (const id of touched) await recalculateInvoiceTotal(id);
    return report;
}
